<template>
  <div>
    <span style="font-weight: bold">{{ t('spareparts.information') }}</span>
    <patternlib-carousel
      arrow-position="center"
      @lhCurrentPage="(event) => awaitHotspotPositions(event.detail)"
      @touchstart.capture.stop="blockSwipe"
      @touchmove.capture.stop="blockSwipe"
      @touchend.capture.stop="blockSwipe"
      @touchcancel.capture.stop="blockSwipe"
      v-if="fileNames.length > 0"
      :height="maxHeight?.toString() + 'px'"
      :auto-sliding-interval="0"
      show-arrows
    >
      <template v-for="(fileName, index) in fileNames" :key="fileName">
        <patternlib-carousel-item :slot="index.toString()" :id="fileName">
          <div
            :style="{
              display: 'inline-block',
              position: 'relative',
              backgroundColor:
                isLoadingHotspot ||
                (hotspotRefs && !!hotspotRefs[index]?.isLoaded.value)
                  ? 'rgba(0, 0, 0, .3490196078)'
                  : ''
            }"
          >
            <img
              ref="imageRefs"
              @vue:mounted="setImage(index, fileName)"
              v-on:load="onImageLoaded(index)"
              :id="`img-${fileName}`"
              :fileName="fileName"
              :style="{
                opacity:
                  isLoadingHotspot ||
                  (hotspotRefs && !!hotspotRefs[index]?.isLoaded.value)
                    ? '35%'
                    : ''
              }"
            />

            <!-- Modal -->
            <HotSpotModal
              ref="hotspotRefs"
              v-show="
                fileName === currentFileName &&
                hotspotRefs &&
                hotspotRefs[index]?.isLoaded.value
              "
            ></HotSpotModal>

            <!-- Generate Hotspots by FileName -->
            <div
              v-for="hotspot in getHotspotsByFileName(fileName).value"
              :id="hotspot.position?.toString()"
              class="hotspot"
              style="position: absolute"
              :style="{
                width: calculatePixelsByXFactor(fileName, hotspot.width),
                height: calculatePixelsByYFactor(fileName, hotspot.height),
                left: calculatePixelsByXFactor(fileName, hotspot.posX),
                top: calculatePixelsByYFactor(fileName, hotspot.posY)
              }"
              @click="hotspot.position && showModal(index, hotspot.position)"
            ></div>
          </div>
        </patternlib-carousel-item>
      </template>
    </patternlib-carousel>
  </div>
</template>

<script setup lang="ts">
  import type { AxiosError } from 'axios';
  import { storeToRefs } from 'pinia';
  import { inject, reactive, ref, useTemplateRef, watch } from 'vue';
  import type { HotSpotShort } from '../api/hau-itde-spareparts-purchase-nonshop-portal-api';
  import { getLocalization } from '../localization/localizationService';
  import { useDataStore } from '../stores/data';
  import HotSpotModal from './Helper/HotSpotModal.vue';

  import { useI18n } from 'vue-i18n';
  const { t } = useI18n();

  const createToast: any = inject('createToast');
  const imageRefs = useTemplateRef<Array<HTMLImageElement>>('imageRefs');
  type HotSpotModalType = InstanceType<typeof HotSpotModal>;
  const hotspotRefs = useTemplateRef<Array<HotSpotModalType>>('hotspotRefs');

  const store = useDataStore();
  const {
    fileNames,
    serviceNumber,
    currentFileName,
    hotspotsPositions,
    hotspotsPositionsPromise,
    isLoadingHotspot
  } = storeToRefs(store);
  const {
    loadExplosionDrawing,
    getHotspotsByFileName,
    setCurrentFileName,
    loadSingleHotSpot,
    getHotSpotByPosition
  } = store;

  const maxHeight = ref<number | undefined>(undefined);
  const resizeEventsCounts = ref(0);
  const scaleMap = reactive(
    new Map<string, { scaleX: number; scaleY: number }>()
  ); //index to scale factor Map
  const emit = defineEmits(['carousel-images-rendered']);

  const blockSwipe = (event: TouchEvent) => {
    event.stopPropagation();
  };

  window.addEventListener('resize', () => {
    resizeCarouselByResizeEvent();
  });

  const showModal = (index: number, position: number) => {
    if (getHotSpotByPosition(position)) {
      hotspotRefs.value![index]!.show(position);
    }

    loadSingleHotSpot(
      serviceNumber.value,
      getLocalization().value.Locale,
      position
    )
      .then(() => {
        hotspotRefs.value![index]!.show(position);
      })
      .catch((err: AxiosError) => {
        createToast(err.message);
      });
  };

  const setImage = async (index: number, fileName: string) => {
    if (!imageRefs.value) {
      return;
    }

    if (!imageRefs.value![index]) {
      return;
    }

    //load images async
    loadExplosionDrawing(fileName).then((base64) => {
      imageRefs.value![index].setAttribute(
        'src',
        'data:image/jpeg;base64,' + base64
      );
    });
  };

  const onImageLoaded = (index: number) => {
    if (index === 0) {
      //reset max Height => indicates that new serialnumber has been loaded
      maxHeight.value = 0;
    }
    const fileName = imageRefs.value![index].getAttribute('fileName');
    resizeCarouselByFile(fileName!);

    //Set HotSpots of first image initially
    if (index === 0) {
      //inital set
      awaitHotspotPositions(index);
    }
  };

  const resizeCarouselByFile = async (fileName: string) => {
    const htmlImageElement = imageRefs.value!.find(
      (htmlImageElement) =>
        htmlImageElement.getAttribute('fileName') === fileName
    );
    if (!htmlImageElement) {
      return;
    }

    //calculate carousel height
    if (htmlImageElement.clientHeight > (maxHeight.value ?? 0)) {
      maxHeight.value = htmlImageElement.clientHeight;
    }

    //calculate scale factors
    let scaleY = htmlImageElement.clientHeight / htmlImageElement.naturalHeight;
    let scaleX = htmlImageElement.clientWidth / htmlImageElement.naturalWidth;
    scaleMap.set(fileName, { scaleX: scaleX, scaleY: scaleY });

    //emit all images rendered
    resizeEventsCounts.value += 1;
    if (resizeEventsCounts.value === fileNames.value.length) {
      await htmlImageElement.decode();
      emit('carousel-images-rendered');
    }
  };

  const resizeCarouselByResizeEvent = () => {
    //calculate new carousel height
    let htmlImageElement = imageRefs.value?.find((htmlImageElement) =>
      Math.max(htmlImageElement.clientHeight)
    );
    if (htmlImageElement) {
      maxHeight.value = htmlImageElement.clientHeight;
    }

    //recalculate all scale factors
    imageRefs.value?.forEach((htmlImageElement) => {
      let scaleY =
        htmlImageElement.clientHeight / htmlImageElement.naturalHeight;
      let scaleX = htmlImageElement.clientWidth / htmlImageElement.naturalWidth;
      scaleMap.set(htmlImageElement.getAttribute('fileName')!, {
        scaleX: scaleX,
        scaleY: scaleY
      });
    });
  };

  const awaitHotspotPositions = async (index: number) => {
    //Wait until hotspots are loaded
    await hotspotsPositionsPromise.value;

    //Get binded FileName
    const fileName: string | null =
      imageRefs.value![index].getAttribute('fileName');

    setCurrentFileName(fileName);

    if (hotspotsPositions.value === undefined) {
      return;
    }

    if (!fileName) {
      return createToast('FileName not found');
    }

    if (fileName in (hotspotsPositions.value || [])) {
      let hotspotsByFilenName: HotSpotShort[] | undefined =
        hotspotsPositions.value[fileName];
      if (hotspotsByFilenName === undefined) {
        return createToast('Hotspots not found for file');
      }
    } else {
      return createToast('Hotspots not found for file');
    }
  };

  const calculatePixelsByXFactor = (
    fileName: string,
    pixels: number | undefined = 0
  ) => {
    if (scaleMap.get(fileName) === undefined) {
      return pixels + 'px';
    }

    return scaleMap.get(fileName)!.scaleX * pixels + 'px';
  };

  const calculatePixelsByYFactor = (
    fileName: string,
    pixels: number | undefined = 0
  ) => {
    if (scaleMap.get(fileName) === undefined) {
      return pixels + 'px';
    }

    return scaleMap.get(fileName)!.scaleY * pixels + 'px';
  };

  watch(fileNames, (newVal) => {
    //fix if explosion drawings height changes on new search
    if (newVal.length === 0) {
      maxHeight.value = 0;
      resizeEventsCounts.value = 0;
    }
  });
</script>

<style scoped>
  .hotspot:hover {
    border: solid #2e8ce6 1px;
  }
</style>
